import { useState, useMemo, useEffect, useCallback } from "react";
import type { CopyNurses } from "../Roster_create";
import type { FullNurseSchemaAddPreceptor } from "../Roster_create";
import { computeCopyNurses } from "@utils/rosterCreate-util.ts";
import type { NurseResponse } from "@/types/nurse-type";

interface UseRosterEditingParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rosterData: any;
  fullNurseSchema: NurseResponse[];
  fullNurseSchemaAddPreceptor: FullNurseSchemaAddPreceptor | undefined;
  curDate: { year: number | null; month: number | null } | null;
}

export default function useRosterEditing({
  rosterData,
  fullNurseSchema,
  fullNurseSchemaAddPreceptor,
  curDate,
}: UseRosterEditingParams) {
  const [copyNurses, setCopyNurses] = useState<CopyNurses>([]);

  // 수정된 셀 추적 (key: "nurseIndex-dayIndex")
  const [modifiedCells, setModifiedCells] = useState<Set<string>>(new Set());

  // 수정된 셀 추가 함수
  const addModifiedCell = useCallback(
    (nurseIndex: number, dayIndex: number) => {
      setModifiedCells((prev) => {
        const next = new Set(prev);
        next.add(`${nurseIndex}-${dayIndex}`);
        return next;
      });
    },
    []
  );

  // 수정된 셀 전체 초기화 (편집 모드 종료, Undo 시 사용)
  const clearModifiedCells = useCallback(() => {
    setModifiedCells(new Set());
  }, []);
  // rosterData가 변경되면 modifiedCells 초기화 (저장 후 데이터 갱신 시)
  useEffect(() => {
    setModifiedCells(new Set());
  }, [rosterData]);

  // copyNurses 초기값을 useMemo로 계산 (유틸 함수 사용)
  const initialCopyNurses = useMemo(() => {
    return computeCopyNurses({
      scheduleData: rosterData ?? null,
      fullNurseSchema,
      fullNurseSchemaAddPreceptor,
      curDate,
    });
  }, [rosterData, fullNurseSchema, fullNurseSchemaAddPreceptor, curDate]);

  // copyNurses 초기값 동기화 (스케줄 데이터나 간호사 스키마 변경 시)
  useEffect(() => {
    if (fullNurseSchema.length > 0 && initialCopyNurses.length > 0) {
      setCopyNurses(initialCopyNurses);
    }
    if (copyNurses.length > 0 && initialCopyNurses.length === 0) {
      return;
    }
  }, [initialCopyNurses, fullNurseSchema.length]);

  // 근무표가 비어있는지 확인
  const isRosterEmpty = useMemo(() => {
    if (!copyNurses || copyNurses.length === 0) return true;
    return copyNurses.every(
      (nurse) =>
        !nurse.schedule ||
        nurse.schedule.every(
          (shift) => shift === "-" || shift === "주" || !shift
        )
    );
  }, [copyNurses]);

  return {
    copyNurses,
    setCopyNurses,
    isRosterEmpty,
    modifiedCells,
    setModifiedCells,
    addModifiedCell,
    clearModifiedCells,
  };
}
